import { cacheService } from '@/utils/cache'
import { CACHE_KEYS, TTL_VALUES } from '../../constants'

/**
 * İstek parametrelerinden önbellek anahtarı oluşturur
 * @private
 * @param {Object} params - İstek parametreleri
 * @returns {Object} Cache için normalize edilmiş parametreler
 */
export const _normalizeParams = function (params = {}) {
  // Temiz bir kopya oluştur
  const normalized = { ...params }

  // Eksik parametreleri varsayılan değerlerle doldur
  normalized.assetId = normalized.assetId || ''
  normalized.q = normalized.q || this.searchQuery || ''
  normalized.page = normalized.page !== undefined ? normalized.page : this.currentPage - 1
  normalized.sort = normalized.sort || this.sortKey || ''
  normalized.order = normalized.order || this.sortOrder || 'DESC'

  // Önbellek versiyonunu SADECE önbellek için kullan, API isteğinde GÖNDERME
  // Bu satırı ekliyoruz ama bu parametre sadece cache key için kullanılacak
  // API isteğinde gönderilmeyecek
  const cacheVersion = this.cacheVersion

  // Filter parametresini tek bir kaynaktan al
  normalized.filter = normalized.filter || normalized.selectedFilter || this.selectedFilter || null

  // Fazla parametreleri temizle
  delete normalized.selectedFilter
  delete normalized.forceRefresh

  // null ve undefined değerleri temizle
  Object.keys(normalized).forEach(key => {
    if (normalized[key] === null || normalized[key] === undefined) {
      delete normalized[key]
    }
  })

  return normalized
}

/**
 * Önbellek versiyonunu artırır
 * @private
 */
export const _incrementCacheVersion = function () {
  // Eğer cache version çok büyükse (1000) veya son artırmadan 1 saat geçtiyse sıfırla
  const oneHourInMs = 60 * 60 * 1000 // 1 saat
  const now = Date.now()
  const timeSinceLastInvalidate = now - this.lastCacheInvalidateTime

  if (this.cacheVersion >= 1000 || timeSinceLastInvalidate > oneHourInMs) {
    // Sıfırlamak yerine timestamp'in son 3 hanesini kullan
    // Böylece her sıfırlamada farklı bir başlangıç değeri olur
    const timestamp = Date.now()
    this.cacheVersion = timestamp % 1000 // Son 3 haneyi al

    if (process.env.VUE_APP_MODE === 'development') {
      console.log(`Önbellek versiyonu sıfırlandı: v${this.cacheVersion}`)
    }
  } else {
    // Normal artırma
    this.cacheVersion++

    if (process.env.VUE_APP_MODE === 'development') {
      console.log(`Önbellek versiyonu artırıldı: v${this.cacheVersion}`)
    }
  }

  // Gereksiz önbellek temizliğini önleme
  this.lastCacheInvalidateTime = now
}

/**
 * İçerik listesi önbelleğini geçersiz kılar
 * @private
 */
export const _invalidateContentListCache = function () {
  // Tüm içerik listesi önbelleğini temizle
  if (process.env.VUE_APP_MODE === 'development') {
    console.log('Önbellek temizleniyor: CONTENT_LIST')
  }
  cacheService.invalidateByPrefix(CACHE_KEYS.CONTENT_LIST)
}

/**
 * Belirli bir içerik detayı önbelleğini geçersiz kılar
 * @private
 * @param {string} contentId - İçerik ID'si
 */
export const _invalidateContentDetailCache = function (contentId) {
  if (contentId) {
    // Cache versiyonunu da dahil ederek önbelleği geçersiz kıl
    cacheService.invalidate(CACHE_KEYS.CONTENT_DETAIL, { contentId, _v: this.cacheVersion })

    if (process.env.VUE_APP_MODE === 'development') {
      console.log(`İçerik detay önbelleği geçersiz kılındı (Content ID: ${contentId})`)
    }
  }
}

/**
 * İlgili tüm önbellekleri temizler
 * @private
 * @param {string} contentId - İçerik ID'si (opsiyonel)
 */
export const _invalidateRelatedCaches = function (contentId = null) {
  // Önbellek versiyonunu artır
  this._incrementCacheVersion()

  console.log(`Önbellek versiyonu artırıldı: ${this.cacheVersion}`)

  // Her zaman içerik listesi önbelleğini temizle
  this._invalidateContentListCache()

  // Eğer belirli bir içerik ID'si verildiyse, o içeriğin detayını da temizle
  if (contentId) {
    this._invalidateContentDetailCache(contentId)
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`İlgili önbellekler temizlendi${contentId ? ` (Content ID: ${contentId})` : ''}`)
  }
}

/**
 * İçerik listesi için TTL değerini seçer
 * @private
 * @param {Object} listData - İçerik listesi verileri
 * @returns {string} TTL değeri
 */
export const _getListTTL = function (listData) {
  // Eğer liste boşsa varsayılan TTL dön
  if (!listData || !listData.detail || listData.detail.length === 0) {
    return TTL_VALUES.CONTENT_LIST.default
  }

  // İçerik türüne göre TTL seç
  const contentTypeCount = {}

  // İçerikleri türlerine göre grupla
  listData.detail.forEach(item => {
    // EntityTypeId = 7: Trend içerikler
    // EntityTypeId = 8: Planlanmış içerikler
    const type = item.entityTypeId === 7 ? 'trending' :
        item.entityTypeId === 8 ? 'scheduled' : 'default'

    contentTypeCount[type] = (contentTypeCount[type] || 0) + 1
  })

  // En çok bulunan türü seç
  let dominantType = 'default'
  let maxCount = 0

  Object.entries(contentTypeCount).forEach(([type, count]) => {
    if (count > maxCount) {
      dominantType = type
      maxCount = count
    }
  })

  return TTL_VALUES.CONTENT_LIST[dominantType]
}

/**
 * İçerik detayı için TTL değerini seçer
 * @private
 * @param {Object} contentData - İçerik detay verileri
 * @returns {string} TTL değeri
 */
export const _getDetailTTL = function (contentData) {
  if (!contentData) {
    return TTL_VALUES.CONTENT_DETAIL.default
  }

  // StatusId = 1: Yayınlanmış içerik
  // StatusId = 2: Taslak içerik
  const ttlKey = contentData.statusId === 1 ? 'published' :
      contentData.statusId === 2 ? 'draft' : 'default'

  return TTL_VALUES.CONTENT_DETAIL[ttlKey]
}